import type {
  ForkStatus,
  ListedFork,
} from "@gearbox-protocol/deploy-tools-shared";
import type { ButtonProps } from "@gearbox-protocol/deploy-tools-ui";
import {
  Button,
  DotsVertical,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@gearbox-protocol/deploy-tools-ui";
import { api } from "api";
import { useCopyToast } from "hooks/useCopyToast";
import type { FC } from "react";
import { Link } from "react-router-dom";
import { useToggle } from "react-use";
import { buildSdkAttachSnippet } from "utils";

import ForkKillDialog from "./ForkKillDialog";
import ForkPauseDialog from "./ForkPauseDialog";
import ForkPinDialog from "./ForkPinDialog";
import ForkRestartDialog from "./ForkRestartDialog";
import ForkUnpinDialog from "./ForkUnpinDialog";
import LowerHealthFactorDialog from "./LowerHealthFactorDialog";
import MakeWithdrawalsClaimableDialog from "./MakeWithdrawalsClaimableDialog";
import RegisterMidasInvestorDialog from "./RegisterMidasInvestorDialog";
import SetZeroLTDialog from "./SetZeroLTDialog";
import StartTaskDialog from "./StartTaskDialog";

interface ForkDropdownMenuProps extends ButtonProps {
  fork: ListedFork | ForkStatus;
}

export const ForkDropdownMenu: FC<ForkDropdownMenuProps> = props => {
  const { fork, ...buttonProps } = props;
  const copy = useCopyToast();

  const [killDialogOpen, toggleKillDialog] = useToggle(false);
  const [pauseDialogOpen, togglePauseDialog] = useToggle(false);
  const [restartDialogOpen, toggleRestartDialog] = useToggle(false);
  const [pinDialogOpen, togglePinDialog] = useToggle(false);
  const [unpinDialogOpen, toggleUnpinDialog] = useToggle(false);
  const [startTaskDialogOpen, toggleStartTaskDialog] = useToggle(false);
  const [zeroLtDialogOpen, toggleZeroLtDialog] = useToggle(false);
  const [lowerHfDialogOpen, toggleLowerHfDialog] = useToggle(false);
  const [claimableDialogOpen, toggleClaimableDialog] = useToggle(false);
  const [midasInvestorDialogOpen, toggleMidasInvestorDialog] = useToggle(false);

  const { alias, paused, persistent } = fork;
  const executionId = paused ? undefined : fork.executionId;
  const isActive = paused ? false : fork.isActive;
  const exOrAlias = executionId || (persistent ? alias : undefined);

  const copyRpcUrl = async () => {
    const status = await api.status(executionId);
    if (!status.paused && status.rpcUrl) {
      await copy(status.rpcUrl, "RPC URL copied");
    }
  };

  const copySdkSnippet = async () => {
    const status = await api.status(executionId);
    if (!status.paused && status.rpcUrl) {
      await copy(buildSdkAttachSnippet(status), "SDK snippet copied");
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" {...buttonProps}>
            <DotsVertical />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-56">
          {isActive && (
            <>
              <DropdownMenuItem onClick={copyRpcUrl}>
                Copy RPC URL
              </DropdownMenuItem>
              <DropdownMenuItem onClick={copySdkSnippet}>
                Copy SDK snippet
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {!!alias && persistent && (isActive || paused) && (
            <DropdownMenuItem asChild>
              <Link to={`/forks/${alias}/update`}>Update</Link>
            </DropdownMenuItem>
          )}

          {isActive && !persistent && (
            <DropdownMenuItem onClick={togglePinDialog}>Pin</DropdownMenuItem>
          )}

          {!!executionId && persistent && (
            <DropdownMenuItem asChild>
              <Link to={`/new-fork?clone=${executionId}`}>Clone</Link>
            </DropdownMenuItem>
          )}

          {!paused && <DropdownMenuSeparator />}
          {isActive && (
            <DropdownMenuItem onClick={toggleStartTaskDialog}>
              Start task
            </DropdownMenuItem>
          )}
          {isActive && (
            <DropdownMenuItem onClick={toggleMidasInvestorDialog}>
              Greenlist Midas investor
            </DropdownMenuItem>
          )}
          {"options" in fork && (
            <DropdownMenuItem
              onClick={toggleZeroLtDialog}
              disabled={!isActive || !fork.options.disableRPCProtection}
            >
              Set Zero LT
            </DropdownMenuItem>
          )}
          {"options" in fork && (
            <DropdownMenuItem
              onClick={toggleLowerHfDialog}
              disabled={!isActive || !fork.options.disableRPCProtection}
            >
              Lower HF
            </DropdownMenuItem>
          )}
          {"options" in fork && (
            <DropdownMenuItem
              onClick={toggleClaimableDialog}
              disabled={!isActive || !fork.options.disableRPCProtection}
            >
              Make withdrawals claimable
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />
          {isActive && persistent && (
            <DropdownMenuItem onClick={toggleUnpinDialog}>
              Unpin
            </DropdownMenuItem>
          )}
          {!paused && (
            <DropdownMenuItem
              onClick={() => {
                console.log("restart");
                toggleRestartDialog();
              }}
            >
              Restart
            </DropdownMenuItem>
          )}
          {!paused && (
            <DropdownMenuItem onClick={toggleKillDialog} disabled={!isActive}>
              Kill
            </DropdownMenuItem>
          )}
          {!!alias && (
            <DropdownMenuItem onClick={togglePauseDialog}>
              {paused ? "Unpause" : "Pause"}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {!!executionId && (
        <ForkKillDialog
          executionId={executionId}
          open={killDialogOpen}
          onOpen={toggleKillDialog}
        />
      )}

      {!!exOrAlias && (
        <ForkRestartDialog
          executionId={exOrAlias}
          open={restartDialogOpen}
          onOpen={toggleRestartDialog}
        />
      )}

      {!!executionId && (
        <ForkPinDialog
          executionId={executionId}
          open={pinDialogOpen}
          onOpen={togglePinDialog}
        />
      )}

      {!!alias && (
        <ForkPauseDialog
          alias={alias}
          paused={paused}
          open={pauseDialogOpen}
          onOpen={togglePauseDialog}
        />
      )}

      <ForkUnpinDialog
        alias={alias!}
        open={unpinDialogOpen}
        onOpen={toggleUnpinDialog}
      />

      {startTaskDialogOpen && !!executionId && (
        <StartTaskDialog
          executionId={executionId}
          toggleOpen={toggleStartTaskDialog}
        />
      )}

      {zeroLtDialogOpen && "options" in fork && (
        <SetZeroLTDialog toggleOpen={toggleZeroLtDialog} />
      )}

      {lowerHfDialogOpen && "options" in fork && (
        <LowerHealthFactorDialog toggleOpen={toggleLowerHfDialog} />
      )}

      {claimableDialogOpen && "options" in fork && (
        <MakeWithdrawalsClaimableDialog toggleOpen={toggleClaimableDialog} />
      )}

      {midasInvestorDialogOpen && !!executionId && (
        <RegisterMidasInvestorDialog
          executionId={executionId}
          toggleOpen={toggleMidasInvestorDialog}
        />
      )}
    </>
  );
};
